import React, { useMemo, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Icon } from '@iconify/react';
import AnimatedLinearProgress from './AnimatedLinearProgress';
import JobsList from './JobsList';
import StatusChip from './StatusChip';
import useResponsive from '../../../../../Hooks/useResponsive';

const GoalView = React.memo(({ goals, expanded, onChange, isChecked, onCheck }) => {
  const smUp = useResponsive('up', 'sm');

  // Prevent function recreation on every render
  const handleCheck = useCallback(
    (e) => {
      e.stopPropagation();
      onCheck?.(e);
    },
    [onCheck]
  );

  const handleFocus = useCallback((e) => e.stopPropagation(), []);

  const mdUp = useResponsive('up', 'md');

  // Memoize tags rendering
  const tagChips = useMemo(() => {
    return goals?.tags?.map((tag, i) => (
      <Box sx={{ pl: 0.3 }} key={i}>
        <Chip size="small" label={tag.name} sx={{ color: tag.color, height: 13, fontSize: 8 }} />
      </Box>
    ));
  }, [goals?.tags]);

  return (
    <Box pb={1}>
      <Stack direction={smUp ? 'row' : 'column'}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={handleCheck}
                disabled={goals.appraisal_status_name !== 'Pending'}
                size="small"
                onClick={handleFocus}
                onFocus={handleFocus}
                sx={{
                  p: 0.3,
                  '& .MuiSvgIcon-root': {
                    fontSize: 16,
                    borderRadius: '4px',
                  },
                }}
              />
            }
            label=""
            sx={{ mr: 1 }}
          />
        </Box>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            mt: 1,
            width: '100%',
          }}
        >
          <Accordion disableGutters elevation={0} expanded={expanded} onChange={onChange}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
              <Stack direction={'column'} spacing={1} width={'100%'}>
                <Box>
                  <Stack direction={'row'} spacing={1} width={'100%'}>
                    <Box
                      sx={{
                        p: 1,
                        height: 30,
                        width: 30,
                        backgroundColor: '#dbf6e5',
                        color: '#119272',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderRadius: '50%',
                      }}
                    >
                      <Icon icon="lucide:goal" width="24" height="24" />
                    </Box>
                    <Box pt={0.5}>
                      <Typography sx={{ fontWeight: 'bolder', fontSize: '0.9rem' }} component="span">
                        {goals.goal_name || 'No Goal Name'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ display: 'flex', width: '100%' }}>
                  <Stack direction={'row'} spacing={1} width={'100%'} justifyContent="flex-end" sx={{ pr: 3 }}>
                    <StatusChip status={goals.appraisal_status_name} />
                    <Chip
                      color="primary"
                      size="small"
                      sx={{ height: 15, fontSize: '0.6rem' }}
                      icon={<Icon icon="stash:data-date-light" width="15" height="15" />}
                      variant="outlined"
                      label={`Start-${goals.goal_start_date || 'No Start Date'}`}
                    />
                    <Chip
                      color="primary"
                      size="small"
                      sx={{ height: 15, fontSize: '0.6rem' }}
                      icon={<Icon icon="stash:data-date-light" width="15" height="15" />}
                      variant="outlined"
                      label={`End-${goals.goal_end_date || 'No End Date'}`}
                    />
                  </Stack>
                </Box>
                <Box>
                  <Chip
                    color="primary"
                    size="small"
                    sx={{ height: 15, fontSize: '0.6rem' }}
                    icon={<Icon icon="solar:user-outline" width="10" height="10" />}
                    variant="outlined"
                    label={`Assigned to -${goals.assignedTo || 'No user'}`}
                  />
                </Box>

                <AnimatedLinearProgress percentage={goals.completedPercentage || 0} date="July 13, 2025" />

                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  {tagChips}
                </Box>
              </Stack>
            </AccordionSummary>

            <Divider />

            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box p={0.5}>
                  <Icon icon="streamline-plump:description-remix" width="24" height="24" />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {goals.goal_description || 'No Description Available'}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                  <Typography sx={{ p: 0.7, fontWeight: 'bolder', fontSize: '0.9rem' }} component="span">
                    Jobs
                  </Typography>
                </Box>
                <Box>
                  <JobsList jobs={goals.jobs || []} />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>
      </Stack>
    </Box>
  );
});

export default GoalView;
